import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOSchema from '@/components/feature/SEOSchema';
import useScrollToTop from '@/hooks/useScrollToTop';
import { siteInfo } from '@/mocks/site';

export default function AboutPage() {
  useScrollToTop();
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = '关于我们 - 虚空韩漫网 | 韩漫在线阅读';
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('company_alt') as string || '').trim();

    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'company_alt' && typeof value === 'string') {
          params.append(key, value);
        }
      });

      const response = await fetch('http://0661yes.cn/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };

  const timeline = [
    { year: '2020', title: '平台创立', desc: '虚空韩漫网正式上线，致力于为中文读者提供优质的韩漫阅读体验' },
    { year: '2021', title: '内容扩展', desc: '漫画库突破1万部，涵盖恋爱、动作、奇幻、悬疑等多种类型' },
    { year: '2022', title: '用户突破', desc: '日活跃用户突破50万，成为国内领先的韩漫在线阅读平台' },
    { year: '2023', title: '技术升级', desc: '全面优化阅读体验，支持下拉式阅读、高清画质、多端同步' },
    { year: '2024', title: '生态完善', desc: '建立完善的创作者合作体系，与韩国多家漫画工作室达成深度合作' },
    { year: '2025', title: '行业领先', desc: '日活用户突破200万，月阅读量超5亿，稳居韩漫阅读平台前列' },
  ];

  const contactInfo = [
    { icon: 'ri-mail-line', label: '电子邮箱', value: siteInfo.email },
    { icon: 'ri-phone-line', label: '联系电话', value: siteInfo.phone },
    { icon: 'ri-map-pin-line', label: '公司地址', value: siteInfo.address },
    { icon: 'ri-wechat-line', label: '微信公众号', value: siteInfo.wechat },
    { icon: 'ri-qq-line', label: 'QQ群', value: siteInfo.qq },
  ];

  return (
    <>
      <SEOSchema type="Organization" />
      <SEOSchema type="WebSite" />
      <SEOSchema type="WebPage" data={{
        name: '关于我们 - 虚空韩漫网',
        description: '了解虚空韩漫网——领先的韩漫在线阅读平台，提供免费韩国漫画大全和无删减下拉式韩漫。查看平台介绍、发展历程和商务合作联系方式。',
        url: 'http://0661yes.cn/about',
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <section className="relative bg-foreground-950 py-16 md:py-24 overflow-hidden">
            <div className="absolute inset-0 opacity-15">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20elegant%20dark%20gradient%20background%20with%20soft%20flowing%20curves%2C%20minimalist%20artistic%20composition%2C%20deep%20charcoal%20with%20subtle%20warm%20accents%2C%20modern%20corporate%20feel%2C%20clean%20abstract%20design%2C%20no%20text%2C%20high%20resolution%2C%20editorial%20quality&width=1920&height=800&seq=about-hero-bg-01&orientation=landscape"
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
            <div className="relative z-10 text-center px-4 md:px-6">
              <p className="text-foreground-400 text-xs font-medium tracking-wider uppercase mb-3">关于我们</p>
              <h1 className="font-heading font-black text-3xl md:text-5xl text-white">虚空韩漫网</h1>
              <p className="font-heading font-semibold text-xl md:text-2xl text-white/80 mt-3">让每一位读者都能享受韩漫的魅力</p>
              <p className="text-foreground-300 text-sm mt-5 max-w-2xl mx-auto leading-relaxed">
                虚空韩漫网成立于2020年，是国内领先的韩漫在线阅读平台。我们致力于为中文读者提供最全面、最新、最高质量的韩国漫画内容，涵盖恋爱、动作、奇幻、悬疑等多种类型，每日更新，完全免费。
              </p>
            </div>
          </section>

          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <Breadcrumb items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]} />
          </div>

          <section className="w-full px-4 md:px-6 max-w-7xl mx-auto py-10 md:py-14">
            <div className="text-center mb-10">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">品牌故事</h2>
              <p className="text-foreground-500 text-sm mt-3 max-w-3xl mx-auto leading-relaxed">
                虚空韩漫网的创立源于创始团队对韩国漫画的热爱。我们发现国内缺乏一个真正专注于韩漫、提供高质量阅读体验的平台。于是，我们组建了一支由漫画爱好者、技术专家和内容运营组成的团队，打造了这个属于所有韩漫爱好者的家园。
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-14">
              {[
                { icon: 'ri-book-open-line', value: siteInfo.stats.totalComics, label: '漫画作品' },
                { icon: 'ri-user-smile-line', value: siteInfo.stats.dailyUsers, label: '日活用户' },
                { icon: 'ri-fire-line', value: siteInfo.stats.monthlyViews, label: '月阅读量' },
                { icon: 'ri-stack-line', value: siteInfo.stats.totalChapters, label: '章节总数' },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-6 rounded-xl bg-background-100">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100">
                    <i className={`${stat.icon} text-accent-600 text-xl`}></i>
                  </div>
                  <div className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">{stat.value}</div>
                  <div className="text-foreground-500 text-xs mt-1">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="text-center mb-10">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">发展历程</h2>
              <p className="text-foreground-500 text-sm mt-2">从0到1，一步一个脚印</p>
            </div>

            <div className="relative max-w-3xl mx-auto">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
              <div className="space-y-8">
                {timeline.map((item, index) => (
                  <div key={item.year} className={`relative flex items-start gap-6 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'} hidden md:block`}>
                      <div className="p-5 rounded-xl bg-background-100">
                        <h3 className="font-heading font-bold text-sm text-foreground-950">{item.title}</h3>
                        <p className="text-foreground-500 text-xs mt-1">{item.desc}</p>
                      </div>
                    </div>
                    <div className="flex-shrink-0 relative z-10">
                      <div className="w-8 h-8 rounded-full bg-accent-500 flex items-center justify-center">
                        <span className="w-2 h-2 rounded-full bg-white"></span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-heading font-black text-xl text-accent-600">{item.year}</span>
                      </div>
                      <div className="p-4 rounded-xl bg-background-100 md:hidden">
                        <h3 className="font-heading font-bold text-sm text-foreground-950">{item.title}</h3>
                        <p className="text-foreground-500 text-xs mt-1">{item.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="bg-background-100 py-10 md:py-14">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-10">
                <div className="flex-1">
                  <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-3">商务合作</h2>
                  <p className="text-foreground-500 text-sm leading-relaxed mb-6">
                    虚空韩漫网欢迎各类商务合作，包括但不限于：版权引进、广告投放、联合推广、内容授权等。我们拥有庞大的用户群体和专业的运营团队，期待与您携手共赢。
                  </p>

                  <div className="space-y-4">
                    {contactInfo.map((item) => (
                      <div key={item.label} className="flex items-center gap-4 p-4 rounded-xl bg-background-50">
                        <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100">
                          <i className={`${item.icon} text-accent-600`}></i>
                        </div>
                        <div>
                          <p className="text-foreground-400 text-xs">{item.label}</p>
                          <p className="text-foreground-800 text-sm font-medium">{item.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 p-5 rounded-xl bg-background-50">
                    <h3 className="font-heading font-semibold text-sm text-foreground-950 mb-2">工作时间</h3>
                    <p className="text-foreground-500 text-xs">周一至周五：9:00 - 18:00</p>
                    <p className="text-foreground-500 text-xs">周六、周日及法定节假日：休息</p>
                  </div>
                </div>

                <div className="flex-1">
                  <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-6">联系我们</h2>
                  <form
                    id="contact-form"
                    onSubmit={handleSubmit}
                    className="space-y-4 p-6 rounded-xl bg-background-50"
                  >
                    <input
                      type="text"
                      name="company_alt"
                      className="antispam-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名 *</label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                          placeholder="您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱 *</label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                          placeholder="hello@0661yes.cn"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="contact-company" className="block text-xs font-medium text-foreground-700 mb-1.5">公司/组织</label>
                      <input
                        id="contact-company"
                        name="company"
                        type="text"
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                        placeholder="您的公司或组织名称"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-topic" className="block text-xs font-medium text-foreground-700 mb-1.5">合作类型 *</label>
                      <select
                        id="contact-topic"
                        name="topic"
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-950 outline-none focus:border-primary-300 transition-colors cursor-pointer"
                      >
                        <option value="">请选择合作类型</option>
                        <option value="版权引进">版权引进</option>
                        <option value="广告投放">广告投放</option>
                        <option value="联合推广">联合推广</option>
                        <option value="内容授权">内容授权</option>
                        <option value="其他合作">其他合作</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">合作描述 *</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors resize-none"
                        placeholder="请描述您的合作意向（500字以内）"
                      ></textarea>
                      <p className="text-foreground-400 text-[10px] mt-1">最多500个字符</p>
                    </div>

                    {formStatus === 'success' && (
                      <div className="p-3 rounded-lg bg-accent-100 text-accent-700 text-xs font-medium">
                        提交成功！我们会尽快与您联系。
                      </div>
                    )}
                    {formStatus === 'error' && (
                      <div className="p-3 rounded-lg bg-red-50 text-red-600 text-xs font-medium">
                        {formError}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full py-3 rounded-lg bg-primary-500 hover:bg-primary-600 text-white font-medium text-sm transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
                    >
                      {formStatus === 'loading' ? '提交中...' : '提交合作意向'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}